/**
 * Per-site identity for SEO.
 *
 * Hantavirus runs three sister sites from the same codebase
 * (trackhantavirus.live, hantavirusrealtime.net, hantavir.us.com).
 * Every canonical URL, every <loc> entry in a sitemap, every
 * Organization JSON-LD `url` MUST flow through these helpers so the
 * same code emits the right hostname under each site's environment.
 *
 * Resolution order for the base URL:
 *   1. `SITE_BASE_URL` — explicit `https://host.tld` from env
 *   2. `SITE_URL`      — legacy alias (already in .env.example)
 *   3. `VITE_APP_URL`  — dev-server fallback
 *   4. `https://${SITE_HOSTNAME}` — derived if hostname is set
 *   5. `https://hantavir.us.com` — final dev/test fallback
 *
 * In the browser these env vars are not present; the canonical/JSON-LD
 * decisions made server-side are passed to the client through
 * `pageContext.documentProps`, so the client never has to re-derive
 * them. If a client-side call ever needs the base URL, the host the
 * page was served from (`window.location.origin`) is used.
 */

export type SiteId = "trackhantavirus" | "hantavirusrealtime" | "hantavir-us";

const DEFAULT_BASE_URL = "https://hantavir.us.com";
const DEFAULT_SITE_ID: SiteId = "hantavir-us";

function readEnv(name: string): string | undefined {
  if (typeof process === "undefined" || !process.env) return undefined;
  const value = process.env[name];
  return value && value.length > 0 ? value : undefined;
}

function stripTrailingSlash(url: string): string {
  return url.endsWith("/") ? url.slice(0, -1) : url;
}

/**
 * The canonical origin (`https://host[:port]`) for the active site.
 * Always returns a URL with no trailing slash. Safe to call from the
 * browser — falls back to `window.location.origin` when env is unset.
 */
export function siteBaseUrl(): string {
  const fromEnv =
    readEnv("SITE_BASE_URL") ?? readEnv("SITE_URL") ?? readEnv("VITE_APP_URL");
  if (fromEnv) return stripTrailingSlash(fromEnv);

  const hostname = readEnv("SITE_HOSTNAME");
  if (hostname) return `https://${stripTrailingSlash(hostname)}`;

  if (typeof window !== "undefined" && window.location?.origin) {
    return stripTrailingSlash(window.location.origin);
  }
  return DEFAULT_BASE_URL;
}

/**
 * The bare hostname (no scheme) for the active site. Used in robots.txt
 * Sitemap lines and in display copy where the scheme is obvious.
 */
export function siteHostname(): string {
  const explicit = readEnv("SITE_HOSTNAME");
  if (explicit) return explicit;
  try {
    return new URL(siteBaseUrl()).hostname;
  } catch {
    return new URL(DEFAULT_BASE_URL).hostname;
  }
}

/**
 * Internal site identifier — used to pick the per-site theme, manifest,
 * favicons, OG defaults. Falls back to `hantavir-us` (the current
 * deploy target per the locked plan) when unset.
 */
export function siteId(): SiteId {
  const id = readEnv("SITE_ID");
  if (id === "trackhantavirus" || id === "hantavirusrealtime" || id === "hantavir-us") {
    return id;
  }
  return DEFAULT_SITE_ID;
}

/**
 * The publisher / Organization display name. Used by JSON-LD
 * `publisher.name`, `<title>` suffixes, and footer chrome.
 */
export function siteName(): string {
  return readEnv("SITE_NAME") ?? "Hantavirus";
}

/**
 * The publisher's contact / abuse email. Used by JSON-LD ContactPoint
 * and the legal pages. Falls back to a placeholder so we never emit
 * `undefined@…`.
 */
export function siteContactEmail(): string {
  return readEnv("SITE_CONTACT_EMAIL") ?? `editorial@${siteHostname()}`;
}

/**
 * Default Open Graph image URL — `<base>/og-default.png`. Per-page
 * overrides are passed via `documentProps.ogImage`.
 */
export function defaultOgImage(): string {
  return `${siteBaseUrl()}/og-default.png`;
}

/**
 * Supported public-facing locales — re-exported from the canonical registry at
 * `apps/web/src/lib/i18n/locales.ts`. Do not edit this list directly — update
 * `LOCALES` in `locales.ts` instead.
 */
import {
  LOCALE_CODES,
  isLocaleCode,
  type LocaleCode,
} from "#/lib/i18n/locales.js";

export const SUPPORTED_LOCALES = LOCALE_CODES;
export type SupportedLocale = LocaleCode;

export function isSupportedLocale(value: string): value is SupportedLocale {
  return isLocaleCode(value);
}
